import { BRAND_HOME_ARIA_LABEL, BRAND_LOGO_TITLE } from "@/lib/marketing-seo";
import { useTranslations } from "next-intl";
import Link from "next/link";
import type { ReactNode } from "react";
import { DotGridBackdrop } from "../../_components/marketing/dot-grid-backdrop";
import {
  BrandLogo,
  DEMO_HREF,
  HERO_PILL_ROW_STYLE,
  LOGIN_HREF,
  PricingHeroPill,
  T,
  heroPills,
  heroTagline,
} from "./pricing-shared";

function HeroNav(): ReactNode {
  const t = useTranslations("public.pricingDesktopHero");
  return (
    <div
      style={{
        position: "relative",
        zIndex: 3,
        maxWidth: 1180,
        margin: "0 auto",
        height: 74,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 28px",
      }}
    >
      <Link
        href="/"
        style={{ textDecoration: "none" }}
        aria-label={BRAND_HOME_ARIA_LABEL}
        title={BRAND_LOGO_TITLE}
      >
        <BrandLogo />
      </Link>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 28,
          fontSize: 14,
          color: "rgba(11,15,25,0.66)",
        }}
      >
        <Link
          href="/"
          className="mkpreise-navlink"
          style={{ color: "rgba(11,15,25,0.66)", textDecoration: "none" }}
        >
          {t("funktionen")}
        </Link>
        <span style={{ color: T.ink, fontWeight: 600 }}>{t("preise")}</span>
        <a
          href={t("sparRechner2")}
          className="mkpreise-navlink"
          style={{ color: "rgba(11,15,25,0.66)", textDecoration: "none" }}
        >
          {t("sparRechner")}
        </a>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
        <Link
          href={LOGIN_HREF}
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: T.ink,
            textDecoration: "none",
          }}
        >
          {t("anmelden")}
        </Link>
        <Link
          href={DEMO_HREF}
          className="mkpreise-btn-dark"
          style={{
            background: T.ink,
            color: "#fff",
            fontSize: 14,
            fontWeight: 600,
            padding: "10px 18px",
            borderRadius: 8,
            textDecoration: "none",
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
          }}
        >
          {t("demoBuchen")} <span style={{ fontSize: 12 }}>↗</span>
        </Link>
      </div>
    </div>
  );
}

/** Desktop hero: nav, headline, big price, module pills, CTAs. */
export function DesktopHero(): ReactNode {
  const tPricingShared = useTranslations("public.pricingShared");
  const t = useTranslations("public.pricingDesktopHero");
  return (
    <div
      // i18n-exempt: data attribute, never rendered — section marker for the screenshot tooling
      data-screen-label="Hero"
      style={{
        position: "relative",
        overflow: "hidden",
        background: "#ffffff",
      }}
    >
      <DotGridBackdrop tone="light" focus="top" style={{ zIndex: 1 }} />
      <HeroNav />
      <div
        style={{
          position: "relative",
          zIndex: 2,
          textAlign: "center",
          padding: "46px 28px 188px",
        }}
      >
        <h1
          style={{
            fontFamily: T.display,
            fontWeight: 600,
            fontSize: 58,
            lineHeight: 1.02,
            letterSpacing: "-0.02em",
            margin: "0 auto",
            maxWidth: 680,
          }}
        >
          {t.rich("eineZahlAllesEnthalten", {
            br: () => <br />,
            em: (chunks) => <em style={{ fontStyle: "normal" }}>{chunks}</em>,
          })}
        </h1>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 18,
            margin: "38px 0 14px",
          }}
        >
          <span
            style={{
              fontFamily: T.display,
              fontWeight: 600,
              fontSize: 190,
              lineHeight: 0.8,
              letterSpacing: "-0.035em",
              color: T.ink,
            }}
          >
            49
          </span>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "center",
              gap: 10,
            }}
          >
            <span
              style={{
                fontFamily: T.display,
                fontStyle: "normal",
                fontWeight: 600,
                fontSize: 60,
                lineHeight: 0.82,
                color: T.ink,
              }}
            >
              {t("cent")}
            </span>
            <span
              style={{
                fontFamily: T.label,
                fontSize: 13,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "rgba(11,15,25,0.55)",
                whiteSpace: "nowrap",
              }}
            >
              {t("proEinheitProMonat")}
            </span>
          </div>
        </div>
        <div
          style={{
            fontFamily: T.sans,
            fontSize: 15.5,
            color: "rgba(11,15,25,0.6)",
            margin: "0 auto 26px",
          }}
        >
          {t.rich("k14CentFuerStellplaetze", {
            em: (chunks) => (
              <span
                style={{
                  fontFamily: T.display,
                  fontStyle: "normal",
                  fontWeight: 600,
                  fontSize: 19,
                  color: T.ink,
                }}
              >
                {chunks}
              </span>
            ),
          })}
        </div>
        <p
          style={{
            fontSize: 18,
            color: "rgba(11,15,25,0.62)",
            margin: "0 auto 28px",
            maxWidth: 460,
          }}
        >
          {heroTagline(tPricingShared)}
        </p>
        <div style={HERO_PILL_ROW_STYLE}>
          {heroPills(tPricingShared).map((pill) => (
            <PricingHeroPill key={pill} label={pill} />
          ))}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 22,
          }}
        >
          <Link
            href={DEMO_HREF}
            className="mkpreise-btn-dark"
            style={{
              background: T.ink,
              color: "#fff",
              padding: "15px 26px",
              borderRadius: 9,
              fontSize: 15,
              fontWeight: 600,
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 9,
            }}
          >
            {t("demoBuchen")} <span style={{ fontSize: 13 }}>↗</span>
          </Link>
          <a
            href={t("enthalten")}
            style={{
              color: T.ink,
              fontSize: 15,
              fontWeight: 600,
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
            }}
          >
            {t.rich("allesWasDabeiIst", {
              arrow: (chunks) => <span>{chunks}</span>,
            })}
          </a>
        </div>
      </div>
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: 200,
          zIndex: 4,
          background:
            "linear-gradient(180deg, rgba(255,255,255,0) 0%, #FFFFFF 90%)",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}
